/**
 * Fleet service - fetches aircraft fleet data from the authenticated /fleet
 * endpoint (backed by the mxo-dev-aether-fleet table, loaded from the Beta Tail
 * List). Falls back to the static Beta Tail List when the API is unavailable or
 * returns no records.
 */
import { createLogger } from '../utils/logger';
import { authedFetch } from './apiClient';
import { STATIC_FLEET } from '../data/fleetData';

const logger = createLogger('FleetService');

/**
 * Represents a fleet record from the APES GetFleets API.
 */
export interface FleetRecord {
  id: number;
  region: string;
  fleetType: string;
  registrationNumber: string;
  aircraftType: string;
  operator: string;
  network: string;
  etops: boolean;
  cat3: boolean;
}

/**
 * Shape of a fleet row as stored in the mxo-dev-aether-fleet table and returned
 * by GET /fleet. Only the three identity columns are populated (the loader
 * sources them from the Beta Tail List); the richer FleetRecord fields
 * (region/fleetType/network/etops/cat3) are not tracked there.
 */
interface FleetApiRecord {
  registration_number?: string;
  operator?: string;
  aircraft_type?: string;
}

/** Maps a raw /fleet row (snake_case) to the FleetRecord shape the UI uses. */
function toFleetRecord(row: FleetApiRecord, index: number): FleetRecord {
  return {
    id: index + 1,
    region: '',
    fleetType: '',
    registrationNumber: row.registration_number || '',
    aircraftType: row.aircraft_type || '',
    operator: row.operator || '',
    network: '',
    etops: false,
    cat3: false,
  };
}

/** Fills optional live fleet fields from the maintained local fleet reference. */
function enrichFleetRecord(record: FleetRecord): FleetRecord {
  const reference: FleetRecord | undefined = STATIC_FLEET.find(
    (candidate: FleetRecord): boolean => candidate.registrationNumber === record.registrationNumber,
  );
  if (!reference) return record;
  return {
    ...record,
    aircraftType: record.aircraftType || reference.aircraftType,
    operator: record.operator || reference.operator,
  };
}

/**
 * Fetches fleet data from the authenticated /fleet endpoint. Returns an array of
 * FleetRecord objects. Falls back to the static Beta Tail List when the API is
 * unavailable (network error / non-OK / auth failure) or returns no records.
 */
export async function fetchFleetData(): Promise<FleetRecord[]> {
  try {
    const response = await authedFetch('/fleet');
    if (!response || !response.ok) {
      logger.warn('Failed to fetch fleet data, using static Beta Tail List', {
        statusCode: response?.status,
      });
      return STATIC_FLEET;
    }
    const data = await response.json();
    const rows: FleetApiRecord[] = data.fleetRecords || [];
    const records: FleetRecord[] = rows
      .map(toFleetRecord)
      .filter((record: FleetRecord): boolean => Boolean(record.registrationNumber))
      .map(enrichFleetRecord);
    return records.length > 0 ? records : STATIC_FLEET;
  } catch (err: unknown) {
    if (err instanceof TypeError) {
      logger.error('Network error fetching fleet data, using static Beta Tail List', {
        error: err.message,
      });
    } else {
      logger.error('Error fetching fleet data, using static Beta Tail List', {
        error: err instanceof Error ? err.message : 'Unknown error',
      });
    }
    return STATIC_FLEET;
  }
}

/**
 * Carrier-code aliases applied when seeding the Carrier field from a fleet
 * operator. The APES fleet reference lists some operators under a name that
 * differs from the canonical code AETHER (and SkyWrench) use. Mirrors the
 * backend `resolveCarrier` aliases.
 *
 *   HAWAIIAN -> ASA : APES lists the A330 operator as "Hawaiian"; AETHER and
 *                     SkyWrench use "ASA" (which selects the A330 knowledge base).
 */
const CARRIER_ALIASES: Record<string, string> = {
  ATI: 'ATN',
  HAWAIIAN: 'ASA',
};

/**
 * Carriers SkyWrench currently supports (i.e. that map to a knowledge base).
 * When an event's carrier is not in this set, the SkyWrench feature is disabled
 * for that event. Codes are canonical (post-normalization).
 */
export const SKYWRENCH_SUPPORTED_CARRIERS: ReadonlySet<string> = new Set([
  'SCX',
  'ABR',
  'FQA', // 737-800BCF
  'ATN', // 767-300F
  'CSB',
  'ABX', // 767-300F
  'HAL',
  'ASA', // A330
]);

/**
 * Normalizes a raw carrier/operator value to its canonical AETHER code (e.g.
 * "Hawaiian" -> "ASA"). Unknown values are returned trimmed and unchanged.
 */
export function normalizeCarrier(carrier: string): string {
  const code = (carrier || '').trim();
  if (!code) return '';
  return CARRIER_ALIASES[code.toUpperCase()] ?? code;
}

/**
 * True when the (normalized) carrier is supported by SkyWrench.
 */
export function isSkyWrenchSupportedCarrier(carrier: string): boolean {
  return SKYWRENCH_SUPPORTED_CARRIERS.has(normalizeCarrier(carrier).toUpperCase());
}

/**
 * Extracts unique tail numbers (registrationNumber) sorted alphabetically (A→Z).
 */
export function getTailNumbers(fleet: FleetRecord[]): string[] {
  const tails = fleet.map((f) => f.registrationNumber).filter(Boolean);
  return Array.from(new Set(tails)).sort((a, b) => a.localeCompare(b));
}

/**
 * Finds the carrier for a given tail number — the fleet `operator`, normalized
 * to its canonical AETHER code (e.g. "Hawaiian" -> "ASA").
 */
export function getOperatorByTail(fleet: FleetRecord[], tailNumber: string): string {
  const record = fleet.find((f) => f.registrationNumber === tailNumber);
  return normalizeCarrier(record?.operator || '');
}

/**
 * Finds the aircraft type for a given tail number.
 */
export function getAircraftTypeByTail(fleet: FleetRecord[], tailNumber: string): string {
  const record = fleet.find((f) => f.registrationNumber === tailNumber);
  return record?.aircraftType || '';
}
